/**
 * Chat Screen Presenter Component
 * Container component that renders both ConversationsContainer and PlaceDetailsContainer
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlaceDetailsContainer,
  PlaceDetailsContainerProps,
} from './place-details/PlaceDetailsContainer';

export interface ChatScreenPresenterProps {
  conversations: React.ReactNode;
  rightSide?: PlaceDetailsContainerProps | null;
}

export const ChatScreenPresenter: React.FC<ChatScreenPresenterProps> = ({
  conversations,
  rightSide,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [wasRightSideVisible, setWasRightSideVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent page scroll on mobile
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';

      const setViewportHeight = () => {
        if (containerRef.current) {
          const viewportHeight =
            window.visualViewport?.height || window.innerHeight;
          const vh = viewportHeight * 0.01;
          document.documentElement.style.setProperty('--vh', `${vh}px`);
          containerRef.current.style.height = `${viewportHeight}px`;
        }
      };

      setViewportHeight();
      requestAnimationFrame(setViewportHeight);
      setTimeout(setViewportHeight, 120);
      window.addEventListener('resize', setViewportHeight);
      window.addEventListener('orientationchange', setViewportHeight);
      window.visualViewport?.addEventListener('resize', setViewportHeight);
      window.visualViewport?.addEventListener('scroll', setViewportHeight);
      window.addEventListener('pageshow', setViewportHeight);
      window.addEventListener('focus', setViewportHeight);
      document.addEventListener('visibilitychange', setViewportHeight);

      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('resize', setViewportHeight);
        window.removeEventListener('orientationchange', setViewportHeight);
        window.visualViewport?.removeEventListener('resize', setViewportHeight);
        window.visualViewport?.removeEventListener('scroll', setViewportHeight);
        window.removeEventListener('pageshow', setViewportHeight);
        window.removeEventListener('focus', setViewportHeight);
        document.removeEventListener('visibilitychange', setViewportHeight);
        document.documentElement.style.removeProperty('--vh');
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobile]);

  // Track when rightSide becomes visible
  useEffect(() => {
    if (rightSide) {
      setWasRightSideVisible(true);
    }
  }, [rightSide]);

  // Reset wasRightSideVisible after exit animation completes
  useEffect(() => {
    if (!rightSide && wasRightSideVisible) {
      const timer = setTimeout(() => {
        setWasRightSideVisible(false);
      }, 400); // Wait for exit animation (300ms) + transition buffer
      return () => clearTimeout(timer);
    }
  }, [rightSide, wasRightSideVisible]);

  // Keep maxWidth stable during exit animation to prevent chat from shrinking
  const maxWidth = rightSide || wasRightSideVisible ? '1354px' : '720px';

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'calc(var(--vh, 1vh) * 100)' : '100dvh',
        minHeight: isMobile ? 'calc(var(--vh, 1vh) * 100)' : '100dvh',
        width: '100%',
        maxWidth,
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
        transition: 'max-width 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      className='relative'
    >
      {/* Main scrollable content */}
      <div
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
        className='gap-4 md:px-4'
      >
        {/* Left Side - Chat */}
        <motion.div
          style={{
            flex: '1 1 0%',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '720px',
            minHeight: 0,
            overflowY: 'auto', // Only the chat scrolls
            // iOS Safari fixes for smooth scrolling
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
          }}
          animate={{
            width: isMobile
              ? '100%'
              : rightSide || wasRightSideVisible
                ? 'auto'
                : '100%',
          }}
          transition={{
            width: {
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1],
            },
          }}
        >
          {conversations}
        </motion.div>

        {/* Right Side - Place Details */}
        <AnimatePresence>
          {rightSide && (
            <motion.div
              key='place-details-panel'
              layout={!isMobile}
              initial={
                isMobile ? { y: '100dvh', opacity: 0 } : { x: 100, opacity: 0 }
              }
              animate={isMobile ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
              exit={
                isMobile ? { y: '100dvh', opacity: 0 } : { x: 100, opacity: 0 }
              }
              transition={{
                layout: {
                  duration: 0.3,
                  ease: [0.16, 1, 0.3, 1],
                },
                x: {
                  duration: 0.3,
                  ease: [0.16, 1, 0.3, 1],
                },
                y: {
                  duration: 0.5,
                  delay: 0.2,
                  ease: [0.16, 1, 0.3, 1],
                },
                opacity: {
                  duration: 0.3,
                  ease: [0.16, 1, 0.3, 1],
                },
              }}
              style={{
                width: '100%',
                minWidth: '280px',
                flex: '1 1 0%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                overflow: 'hidden',
                boxSizing: 'border-box',
                willChange: isMobile ? 'transform, opacity' : 'auto',
                backfaceVisibility: isMobile ? 'hidden' : 'visible',

                position: isMobile ? 'fixed' : 'relative',
                inset: isMobile ? '3.5rem 0 0 0' : undefined,
                height: isMobile ? 'auto' : '100dvh',
                paddingLeft: isMobile ? '16px' : 0,
                paddingRight: isMobile ? '16px' : 0,
                paddingTop: isMobile ? 0 : '16px',
                paddingBottom: isMobile ? 0 : '16px',
              }}
              className='w-full md:flex-1 md:max-w-[618px] [&::-webkit-scrollbar]:hidden z-10 md:z-auto bg-white md:bg-transparent'
            >
              <PlaceDetailsContainer {...rightSide} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
